import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { CaseListScope, CaseSummary } from '@/lib/api-types'
import { formatDate, relativeTime } from '@/lib/format'
import { caseChipClasses, caseTypeLabel } from '@/lib/status'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table'
import { EmptyState } from '@/components/ui/empty-state'
import { ErrorState } from '@/components/ui/error-state'
import { PageHeader } from '@/components/ui/page-header'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { StatusChip } from '@/components/ui/status-chip'
import { Toolbar } from '@/components/ui/toolbar'

/** The two-way status filter the UI exposes (the API also takes 'all'). */
type StatusFilter = 'open' | 'closed'

function parseScope(raw: string | null): CaseListScope {
  return raw === 'all' ? 'all' : 'mine'
}

function parseStatus(raw: string | null): StatusFilter {
  return raw === 'closed' ? 'closed' : 'open'
}

export function RequestsListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const scope = parseScope(searchParams.get('scope'))
  const status = parseStatus(searchParams.get('status'))

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['cases', scope, status],
    queryFn: ({ signal }) => api.listCases(scope, status, { signal }),
    staleTime: 15_000,
  })
  // The opposite scope rides along for its pill count and for telling "you
  // have none" apart from "your account has none" in the empty states.
  const otherScope: CaseListScope = scope === 'mine' ? 'all' : 'mine'
  const otherQuery = useQuery({
    queryKey: ['cases', otherScope, status],
    queryFn: ({ signal }) => api.listCases(otherScope, status, { signal }),
    staleTime: 15_000,
  })

  function setParam(key: 'scope' | 'status', value: string, defaultValue: string) {
    const params = new URLSearchParams(searchParams)
    if (value === defaultValue) params.delete(key)
    else params.set(key, value)
    setSearchParams(params, { replace: true })
  }

  const counts = data?.counts
  const scopeTotal = (s: CaseListScope): number | undefined =>
    s === scope ? counts?.all : otherQuery.data?.counts.all
  const scopeLabel = (s: CaseListScope): string => {
    const base = s === 'mine' ? 'Yours' : 'Everyone'
    const total = scopeTotal(s)
    return total === undefined ? base : `${base} (${total})`
  }
  const statusLabel = (f: StatusFilter): string => {
    const base = f === 'open' ? 'Open' : 'Closed'
    return counts ? `${base} (${counts[f]})` : base
  }

  // Waiting-on-you rows pin to the top regardless of the server's
  // updatedAt order; columns are deliberately not sortable so nothing can
  // shuffle them back down.
  const cases = data?.cases ?? []
  const waiting = cases.filter((c) => c.waitingOnYou)
  const rows = [...waiting, ...cases.filter((c) => !c.waitingOnYou)]

  const columns: DataTableColumn<CaseSummary>[] = [
    {
      id: 'number',
      header: 'Request #',
      widthClassName: 'w-28',
      nowrap: true,
      cell: (c) => (
        <Link
          to={`/requests/${c.id}`}
          onClick={(event) => event.stopPropagation()}
          className="rounded-sm font-mono text-[0.8125rem] text-inkMid no-underline underline-offset-4 hover:text-brand hover:underline"
        >
          #{c.caseNumber}
        </Link>
      ),
    },
    {
      id: 'subject',
      header: 'Subject',
      flex: true,
      widthClassName: 'min-w-48',
      cell: (c) => (
        <span className="text-sm text-ink" title={c.subject}>
          {c.subject}
        </span>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      widthClassName: 'w-36',
      nowrap: true,
      cell: (c) => <span className="text-sm text-inkMid">{caseTypeLabel(c.type)}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      nowrap: true,
      cell: (c) => <StatusChip label={c.statusLabel} chipClasses={caseChipClasses(c.status)} />,
    },
    // The Due column only exists while a row carries a due date (questionnaires).
    ...(cases.some((c) => c.dueDate)
      ? [
          {
            id: 'due',
            header: 'Due',
            widthClassName: 'w-28',
            nowrap: true,
            cell: (c) => (
              <span className="text-sm text-inkMid">{c.dueDate ? formatDate(c.dueDate) : '—'}</span>
            ),
          } satisfies DataTableColumn<CaseSummary>,
        ]
      : []),
    ...(scope === 'all'
      ? [
          {
            id: 'submittedBy',
            header: 'Submitted by',
            widthClassName: 'w-36',
            nowrap: true,
            cell: (c) => <span className="text-sm text-inkMid">{c.submittedByName ?? '—'}</span>,
          } satisfies DataTableColumn<CaseSummary>,
        ]
      : []),
    {
      id: 'updated',
      header: 'Updated',
      widthClassName: 'w-32',
      nowrap: true,
      cell: (c) => <span className="text-sm text-mute">{relativeTime(c.updatedAt)}</span>,
    },
  ]

  return (
    <div className="mx-auto max-w-data">
      <PageHeader
        title="Requests"
        description="Ask Green Line for documents, questionnaires, quality help, and more — and track every reply here."
        actions={
          <Button asChild>
            <Link to="/requests/new">New request</Link>
          </Button>
        }
      />

      <Toolbar className="mt-6">
        <SegmentedControl
          label="Whose requests"
          value={scope}
          onChange={(next) => setParam('scope', next, 'mine')}
          options={(['mine', 'all'] as const).map((s) => ({ value: s, label: scopeLabel(s) }))}
        />
        <SegmentedControl
          label="Filter requests"
          value={status}
          onChange={(next) => setParam('status', next, 'open')}
          options={(['open', 'closed'] as const).map((f) => ({
            value: f,
            label: statusLabel(f),
            withDot: f === 'open',
          }))}
        />
      </Toolbar>

      {waiting.length > 0 ? (
        <div
          role="status"
          className="mt-4 rounded-md border border-amber/40 bg-amber/10 px-4 py-3 text-sm text-inkMid"
        >
          {waiting.length === 1
            ? '1 request is waiting on you — it’s pinned to the top.'
            : `${waiting.length} requests are waiting on you — they’re pinned to the top.`}
        </div>
      ) : null}

      <div className="mt-4">
        <DataTable<CaseSummary>
          columns={columns}
          rows={rows}
          rowKey={(c) => c.id}
          onRowClick={(c) => navigate(`/requests/${c.id}`)}
          rowClassName={(c) =>
            c.waitingOnYou ? 'border-l-2 border-l-amber bg-amber/5' : undefined
          }
          caption="Your requests with Green Line"
          loading={isPending}
          loadingLabel="Loading your requests…"
          error={
            isError ? (
              <ErrorState message="We couldn’t load your requests." onRetry={() => void refetch()} />
            ) : undefined
          }
          empty={
            <RequestsEmptyState
              scope={scope}
              status={status}
              counts={counts}
              accountHasAny={otherQuery.isSuccess ? otherQuery.data.counts.all > 0 : undefined}
              onSeeEveryone={() => setParam('scope', 'all', 'mine')}
              onSwapStatus={() =>
                setParam('status', status === 'open' ? 'closed' : 'open', 'open')
              }
            />
          }
          renderCard={(c) => <RequestCard request={c} />}
        />
        {data?.capped ? (
          <p className="mt-3 text-xs text-mute">
            Showing the 500 most recently updated requests.
          </p>
        ) : null}
      </div>
    </div>
  )
}

function RequestsEmptyState({
  scope,
  status,
  counts,
  accountHasAny,
  onSeeEveryone,
  onSwapStatus,
}: {
  scope: CaseListScope
  status: StatusFilter
  counts: { open: number; closed: number; all: number } | undefined
  /**
   * Whether the account (all scope) holds any cases — undefined until that
   * query has SUCCEEDED. Only consulted under scope=mine; a pending or
   * failed cross-scope call must never be read as "the account has none".
   */
  accountHasAny: boolean | undefined
  onSeeEveryone: () => void
  onSwapStatus: () => void
}) {
  const scopeHasAny = (counts?.all ?? 0) > 0

  // Nobody on the account has ever sent one.
  const accountEmptyState = (
    <EmptyState
      title="No requests yet"
      action={
        <Button asChild>
          <Link to="/requests/new">Start a request</Link>
        </Button>
      }
    >
      Ask for documents, hand over a questionnaire, report a quality issue, or put a question
      to the Green Line team — replies land right here.
    </EmptyState>
  )

  if (!scopeHasAny && scope === 'mine') {
    // Your scope is empty but colleagues have requests — one click to All.
    if (accountHasAny === true) {
      return (
        <EmptyState
          title="Nothing from you yet"
          action={
            <Button variant="quiet" size="sm" onClick={onSeeEveryone}>
              See everyone’s requests
            </Button>
          }
        >
          Colleagues at your account have open requests — you can view and join any of them.
        </EmptyState>
      )
    }
    // Claim a truly empty account only off a successful zero-count all-scope
    // call; while it's pending or errored, fall through to the neutral
    // filtered empty state rather than guess.
    if (accountHasAny === false) return accountEmptyState
  } else if (!scopeHasAny) {
    // scope=all: this query IS the account view, and it succeeded with zero.
    return accountEmptyState
  }

  // The scope has cases — just not under this status filter (or the
  // cross-scope check hasn't settled).
  return (
    <EmptyState
      title={status === 'open' ? 'No open requests right now.' : 'No closed requests yet.'}
      action={
        <Button variant="quiet" size="sm" onClick={onSwapStatus}>
          {status === 'open' ? 'See closed requests' : 'See open requests'}
        </Button>
      }
    />
  )
}

/** Narrow-screen fallback: one tappable card per request. */
function RequestCard({ request }: { request: CaseSummary }) {
  return (
    <Link to={`/requests/${request.id}`} className="block no-underline">
      <Card
        padding="sm"
        className={cn(
          'transition-colors duration-[180ms] ease-editorial hover:border-mute hover:bg-cream',
          request.waitingOnYou && 'border-l-2 border-l-amber bg-amber/5',
        )}
      >
        <span className="flex items-baseline justify-between gap-3">
          <span className="font-mono text-[0.8125rem] text-inkMid">#{request.caseNumber}</span>
          <span className="text-xs text-mute">{relativeTime(request.updatedAt)}</span>
        </span>
        <span className="mt-1.5 block text-sm font-medium text-ink">{request.subject}</span>
        <span className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <StatusChip
            label={request.statusLabel}
            chipClasses={caseChipClasses(request.status)}
          />
          <span className="text-xs text-mute">{caseTypeLabel(request.type)}</span>
          {request.dueDate ? (
            <span className="text-xs text-mute">due {formatDate(request.dueDate)}</span>
          ) : null}
          {request.submittedByName ? (
            <span className="text-xs text-mute">by {request.submittedByName}</span>
          ) : null}
        </span>
      </Card>
    </Link>
  )
}
